import React, { useEffect, useState } from "react";
import api, { API, getToken } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Check, X, Clock, User as UserIcon, ImageIcon } from "lucide-react";
import { toast } from "sonner";

function StatusBadge({ status }) {
  const map = {
    pending: "bg-amber-500/10 text-amber-400 border-amber-500/20",
    awaiting_proof: "bg-zinc-800 text-zinc-400 border-zinc-700",
    approved: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
    rejected: "bg-red-500/10 text-red-400 border-red-500/20",
  };
  const label = {
    pending: "PENDIENTE",
    awaiting_proof: "ESPERANDO",
    approved: "APROBADO",
    rejected: "RECHAZADO",
  };
  return (
    <span className={`text-[10px] font-mono font-bold px-2 py-1 rounded-sm border ${map[status]}`}>
      {label[status] || status}
    </span>
  );
}

export default function PendingPayments() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rejectTx, setRejectTx] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [previewImg, setPreviewImg] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/transactions?status=pending&page_size=50");
      const r2 = await api.get("/transactions?status=awaiting_proof&page_size=20");
      setItems([...(r.data.items || []), ...(r2.data.items || [])]);
    } catch (e) {
      toast.error("Error cargando pagos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const approve = async (tx) => {
    try {
      await api.post(`/transactions/${tx.id}/approve`, { note: null });
      toast.success("Pago aprobado y créditos entregados");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al aprobar");
    }
  };

  const reject = async () => {
    if (!rejectReason.trim()) {
      toast.error("Indica un motivo");
      return;
    }
    try {
      await api.post(`/transactions/${rejectTx.id}/reject`, { reason: rejectReason });
      toast.success("Pago rechazado");
      setRejectTx(null);
      setRejectReason("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const proofUrl = (tx) =>
    tx.proof_file_id
      ? `${API}/transactions/${tx.id}/proof?token=${getToken()}`
      : null;

  return (
    <div className="space-y-6 max-w-[1400px]" data-testid="pending-payments-page">
      <div>
        <div className="overline mb-2">Cola crítica</div>
        <h1 className="font-display font-black text-2xl sm:text-3xl lg:text-4xl tracking-tight">Aprobar pagos</h1>
        <p className="text-sm text-zinc-500 mt-2">
          Revisa cada comprobante y acredita los créditos/membresía al usuario en Telegram.
        </p>
      </div>

      {loading ? (
        <div className="text-sm text-zinc-500">Cargando…</div>
      ) : items.length === 0 ? (
        <div
          className="border border-dashed border-zinc-800 rounded-sm p-12 text-center"
          data-testid="pending-empty"
        >
          <Check className="w-10 h-10 mx-auto text-emerald-500 mb-3" strokeWidth={1.5} />
          <div className="text-sm text-zinc-400">No hay pagos pendientes por revisar.</div>
          <div className="text-xs text-zinc-600 mt-1">Todo al día.</div>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {items.map((tx) => (
            <div
              key={tx.id}
              className="bg-zinc-900/70 border border-zinc-800 rounded-sm hover:border-zinc-700 transition-all grid grid-cols-1 md:grid-cols-[120px_1fr_auto] gap-4 p-4"
              data-testid={`pending-payment-row-${tx.id}`}
            >
              {/* Thumbnail */}
              <button
                className="w-28 h-28 rounded-sm bg-zinc-950 border border-zinc-800 overflow-hidden flex items-center justify-center hover:border-[#2AABEE] transition-colors"
                onClick={() => proofUrl(tx) && setPreviewImg(proofUrl(tx))}
                data-testid={`pending-payment-thumb-${tx.id}`}
              >
                {tx.proof_file_id ? (
                  <img src={proofUrl(tx)} alt="proof" className="w-full h-full object-cover" />
                ) : (
                  <div className="flex flex-col items-center text-zinc-600">
                    <ImageIcon size={22} />
                    <span className="text-[10px] mt-1">sin comprobante</span>
                  </div>
                )}
              </button>

              {/* Middle: info */}
              <div className="min-w-0 flex flex-col justify-between">
                <div className="flex items-start gap-3 mb-2">
                  <StatusBadge status={tx.status} />
                  <span className="text-xs font-mono text-zinc-500">#{tx.reference}</span>
                </div>
                <div className="font-display font-bold text-xl mb-1 truncate">
                  {tx.item_name}
                  <span className="ml-2 text-sm font-mono text-zinc-500 font-normal">
                    ({tx.type === "credit" ? `${tx.credits_or_days} créd.` : `${tx.credits_or_days} días`})
                  </span>
                </div>
                <div className="text-sm text-zinc-400 flex items-center gap-3 flex-wrap">
                  <span className="flex items-center gap-1.5">
                    <UserIcon size={13} />
                    {tx.telegram_username ? `@${tx.telegram_username}` : `ID ${tx.telegram_id}`}
                  </span>
                  <span className="text-zinc-700">·</span>
                  <span className="font-mono text-emerald-400">${tx.price_usd} USD</span>
                  <span className="text-zinc-700">/</span>
                  <span className="font-mono text-emerald-400">${tx.price_mxn} MXN</span>
                  <span className="text-zinc-700">·</span>
                  <span className="flex items-center gap-1 text-zinc-500 text-xs">
                    <Clock size={12} />
                    {new Date(tx.created_at).toLocaleString("es-MX")}
                  </span>
                </div>
                {tx.proof_note && (
                  <div className="text-xs text-zinc-500 mt-2 italic line-clamp-2">
                    Nota del usuario: "{tx.proof_note}"
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex md:flex-col gap-2 md:justify-center">
                <Button
                  onClick={() => approve(tx)}
                  data-testid={`approve-payment-${tx.id}`}
                  className="bg-emerald-500 hover:bg-emerald-600 text-black font-bold rounded-sm"
                  disabled={tx.status === "awaiting_proof"}
                >
                  <Check size={15} className="mr-1.5" />
                  Aprobar
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => setRejectTx(tx)}
                  data-testid={`reject-payment-${tx.id}`}
                  className="border border-red-500/40 text-red-400 hover:bg-red-500/10 rounded-sm"
                >
                  <X size={15} className="mr-1.5" />
                  Rechazar
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Reject dialog */}
      <Dialog open={!!rejectTx} onOpenChange={() => setRejectTx(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-display">Rechazar pago</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-sm text-zinc-400">
              Se le notificará al usuario en Telegram con el motivo del rechazo.
            </div>
            <Textarea
              placeholder="Motivo del rechazo (ej: No se visualiza el comprobante)"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              data-testid="reject-reason-textarea"
              className="bg-zinc-950 border-zinc-800 rounded-sm min-h-[100px]"
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRejectTx(null)}>
              Cancelar
            </Button>
            <Button
              onClick={reject}
              data-testid="confirm-reject-button"
              className="bg-red-500 hover:bg-red-600 rounded-sm"
            >
              Rechazar pago
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Image preview */}
      <Dialog open={!!previewImg} onOpenChange={() => setPreviewImg(null)}>
        <DialogContent className="bg-zinc-950 border-zinc-800 rounded-sm max-w-4xl">
          <DialogHeader>
            <DialogTitle className="font-display">Comprobante</DialogTitle>
          </DialogHeader>
          {previewImg && (
            <img src={previewImg} alt="comprobante" className="w-full max-h-[80vh] object-contain" />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
